'use client';

import { BookOpen, ChevronDown, CircleHelp, X } from 'lucide-react';
import { useState } from 'react';

import type { TaskFile } from '@/entities/interview-task';

type PanelHeaderProps = {
	mode: 'quiz' | 'file';
	openedFile: TaskFile | null;
	pinnedFiles: TaskFile[];
	onSelectFile: (file: TaskFile) => void;
	onClose: () => void;
};

const isBook = (kind: TaskFile['kind']) =>
	kind === 'theory' || kind === 'markdown';

const getVersion = (file: TaskFile): number =>
	'version' in file ? file.version : 1;

export function PanelHeader({
	mode,
	openedFile,
	pinnedFiles,
	onSelectFile,
	onClose,
}: PanelHeaderProps) {
	const [menuOpen, setMenuOpen] = useState(false);

	const isQuiz = mode === 'quiz';
	const inPinnedGroup =
		!isQuiz &&
		!!openedFile &&
		pinnedFiles.length > 1 &&
		pinnedFiles.some((f) => f.name === openedFile.name);

	return (
		<div className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-4 bg-white px-3.5">
			{isQuiz ? (
				<CircleHelp
					size={18}
					strokeWidth={2}
					className="shrink-0 text-gray-3"
				/>
			) : openedFile && isBook(openedFile.kind) ? (
				<BookOpen size={18} strokeWidth={2} className="shrink-0 text-gray-3" />
			) : (
				<span className="shrink-0 font-mono text-sm font-semibold tracking-tight text-gray-3">
					{'</>'}
				</span>
			)}

			<div className="min-w-0 flex-1">
				{isQuiz ? (
					<span className="text-base font-semibold text-black">
						Проверь себя
					</span>
				) : inPinnedGroup && openedFile ? (
					<div className="relative">
						<button
							type="button"
							onClick={() => setMenuOpen((v) => !v)}
							className="-mx-1.5 flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-colors hover:bg-gray-5"
						>
							<span className="truncate text-base font-medium text-black">
								{openedFile.name}
							</span>
							<ChevronDown
								size={14}
								strokeWidth={2.2}
								className={`shrink-0 text-gray-3 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
							/>
						</button>
						{menuOpen && (
							<>
								<div
									onClick={() => setMenuOpen(false)}
									className="fixed inset-0 z-30"
								/>
								<div className="absolute left-0 top-full z-40 mt-1.5 min-w-[240px] rounded-lg border border-gray-4 bg-white p-1 shadow-md">
									{pinnedFiles.map((file) => {
										const isCurrent = file.name === openedFile.name;
										return (
											<button
												type="button"
												key={file.name}
												onClick={() => {
													onSelectFile(file);
													setMenuOpen(false);
												}}
												className={`flex w-full cursor-pointer items-center gap-2 truncate rounded-md px-2.5 py-2 text-left text-[13.5px] transition-colors ${isCurrent ? 'bg-gray-5 font-medium text-black' : 'text-gray-3 hover:bg-gray-5 hover:text-black'}`}
											>
												{isBook(file.kind) ? (
													<BookOpen
														size={14}
														strokeWidth={2}
														className="shrink-0 text-gray-4"
													/>
												) : (
													<span className="shrink-0 font-mono text-[10px] font-semibold tracking-tight text-gray-4">
														{'</>'}
													</span>
												)}
												<span className="truncate">{file.name}</span>
											</button>
										);
									})}
								</div>
							</>
						)}
					</div>
				) : openedFile ? (
					<div className="flex min-w-0 items-baseline gap-2">
						<span className="truncate text-base font-medium text-black">
							{openedFile.name}
						</span>
						{getVersion(openedFile) > 1 && (
							<span className="shrink-0 text-sm text-gray-4">
								Отправка {getVersion(openedFile) - 1}
							</span>
						)}
					</div>
				) : null}
			</div>

			<button
				type="button"
				onClick={onClose}
				title="Закрыть"
				className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md bg-transparent text-gray-3 transition-colors hover:bg-gray-5"
			>
				<X size={16} strokeWidth={2} />
			</button>
		</div>
	);
}
